// In-game view capture, desktop AND XR. The Pico's own screenshot drops the
// browser out of VR; this one renders the CURRENT view into a render target
// inside the frame, right after the main render, and uploads the PNG to the
// dev server (POST /dev/shot → crates/game/tests/device/xr-shots/<name>.png,
// see crate.js). In XR, three copies the headset pose + the two eyes' union
// projection into game.camera on every frame (WebXRManager.updateUserCamera),
// so rendering that camera with xr disabled gives a single-eye-like image of
// exactly what the wearer sees (HUD included — it is a camera child).
import { RGBAFormat, UnsignedByteType, WebGLRenderTarget } from 'three';

const SHOT_HEIGHT = 1600;

let pending = null; // { promise, resolve, reject, zoom } of the requested shot
let rt = null;

// zoom > 1 narrows the field of view about its centre (the HUD text becomes legible in the
// file; at 1 the XR union view is ~104° and 16 px labels end up ~10 px in the image)
export function requestScreenshot({ zoom = 1 } = {}) {
	if (pending) return pending.promise;
	let resolve, reject;
	const promise = new Promise((a, b) => { resolve = a; reject = b; });
	pending = { promise, resolve, reject, zoom };
	return promise;
}

// Called by the animation loop right after renderer.render(scene, camera).
export function captureIfRequested(renderer, scene, camera) {
	if (!pending) return;
	const job = pending;
	pending = null;
	try {
		const m = camera.projectionMatrix.elements;
		const aspect = m[5] / m[0]; // (r-l)/(t-b): right for the asymmetric XR union projection too
		const h = SHOT_HEIGHT, w = Math.round(h * aspect);
		if (!rt || rt.width !== w || rt.height !== h) {
			rt?.dispose();
			rt = new WebGLRenderTarget(w, h, {
				colorSpace: renderer.outputColorSpace, type: UnsignedByteType, format: RGBAFormat,
				depthBuffer: true, stencilBuffer: true,
			});
			// three applies the output colour space + tone mapping only to the canvas
			// and to XR eye buffers (`isXRRenderTarget`) — mark ours the same, so the
			// shot gets the same programs (no recompile) and the same look.
			rt.isXRRenderTarget = true;
		}
		const inXR = renderer.xr.isPresenting;
		const prevRT = renderer.getRenderTarget();
		const xrWasEnabled = renderer.xr.enabled;
		// (rendering one of renderer.xr.getCamera().cameras[] here instead gives garbage — their
		// matrices are only valid inside three's own XR pass; tried 2026-10-10)
		renderer.xr.enabled = false; // render game.camera itself, not the XR camera array
		renderer.setRenderTarget(rt);
		const z = job.zoom;
		if (z !== 1) { m[0] *= z; m[5] *= z; camera.projectionMatrixInverse.copy(camera.projectionMatrix).invert(); }
		renderer.render(scene, camera);
		if (z !== 1) { m[0] /= z; m[5] /= z; camera.projectionMatrixInverse.copy(camera.projectionMatrix).invert(); }
		renderer.setRenderTarget(prevRT);
		renderer.xr.enabled = xrWasEnabled;
		const pixels = new Uint8Array(w * h * 4);
		// synchronous readback on purpose: with `isXRRenderTarget` set, three r183's
		// readRenderTargetPixelsAsync rejects (with undefined) on desktop and Pico alike
		// (measured 2026-10-11: async → REJECT, sync → 75 ms on the Pico, 1.7 s on swiftshader)
		renderer.readRenderTargetPixels(rt, 0, 0, w, h, pixels);
		upload(pixels, w, h, inXR ? 'xr' : '2d').then(job.resolve, job.reject);
	} catch (e) {
		job.reject(e);
	}
}

async function upload(pixels, w, h, mode) {
	// WebGL rows are bottom-up; alpha forced opaque (the canvas clears transparent)
	const image = new ImageData(w, h);
	const out = image.data, row = w * 4;
	for (let y = 0; y < h; y++) {
		out.set(pixels.subarray((h - 1 - y) * row, (h - y) * row), y * row);
		for (let x = 3; x < row; x += 4) out[y * row + x] = 255;
	}
	const canvas = document.createElement('canvas');
	canvas.width = w;
	canvas.height = h;
	canvas.getContext('2d').putImageData(image, 0, 0);
	const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
	const bundle = (document.querySelector('client-build-hash')?.textContent.match(/bundle (\w+)/) || [])[1] || 'nobundle';
	const name = `${new Date().toISOString().replace(/[:.]/g, '-')}-${mode}-${bundle}.png`;
	const response = await fetch('/dev/shot?name=' + encodeURIComponent(name), {
		method: 'POST', body: blob, headers: { 'content-type': 'image/png' },
	});
	const text = await response.text();
	if (!response.ok) throw new Error(`shot upload failed: ${response.status} ${text}`);
	console.log('[screenshot]', text, `${w}×${h}`);
	return name;
}
globalThis.__bundleMarker = 1793002200; // bundle-freshness probe (tests/device: plain reload must pick up a new bundle)
